import * as React from "react";
import { useState } from "react";
import styled from "@mui/material/styles/styled";
import MenuItem from "@mui/material/MenuItem";
import { DDSortType } from "fe-common/hooks/sortFeaturesWithCrs";
import { DesktopOnly } from "../util/desktopOnly";
import { MobileOnly } from "../util/mobileOnly";
import { Divider, Drawer, IconButton, Menu } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import CheckIcon from "@mui/icons-material/Check";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import SortIconSVG from "../../../assets/sortIcon.svg";
import { useScreenSize } from "../util/useScreenSize";

//region [[ Styles ]]

const SortSelectView = styled("div")(({ theme }) => ({
  marginLeft: "2px",
  marginRight: "2px",
}));

const SortButton = styled(IconButton)(({ theme }) => ({
  fontSize: "16px",
  [theme.breakpoints.up(MobileView)]: {
    height: "42px",
    width: "42px",
  },
}));

const SortIcon = styled("span")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    color: "white",
    marginLeft: "10px",
    filter: "brightness(0) invert(1)",
  },
}));

const SortIconImg = styled("img")(({ theme }) => ({
  marginTop: "6px",
  [theme.breakpoints.down(MobileView)]: {
    height: "20px",
    marginTop: "6px",
  },
}));

const IconContainer = styled("div")(({ theme }) => ({
  minWidth: "32px",
  marginTop: "4px",
  marginRight: "0px",
}));

const DrawerTitle = styled("div")(({ theme }) => ({
  fontSize: "16px",
  paddingLeft: "16px",
  paddingTop: "10px",
}));

const ListItemIconStyled = styled("div")(({ theme }) => ({}));

const SortTypeSelection = styled(ListItemText)(
  ({ selected }: { selected: boolean }) => ({
    color: selected ? RosebudColorMap.green2 : RosebudColorMap.gray2,
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface SortSelectProps {
  handleSortChange: (sortType: DDSortType) => void;
  sortType: DDSortType;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SortSelect = ({ ...props }: SortSelectProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isMobile } = useScreenSize();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectSort = (sortType: DDSortType) => {
    setAnchorEl(null);
    props.handleSortChange(sortType);
  };

  const toggleDrawer = (open: boolean) => (
    event: React.KeyboardEvent | React.MouseEvent,
  ) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    setDrawerOpen(open);
  };

  const getIcon = (sortType: DDSortType) => {
    return props.sortType === sortType ? (
      <IconContainer>
        <CheckIcon />
      </IconContainer>
    ) : (
      <IconContainer />
    );
  };

  const list = () => (
    <div onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
      <List>
        <ListItem
          button
          onClick={() => {
            handleSelectSort(DDSortType.BySuspicionIndex);
          }}
        >
          <ListItemIconStyled>
            {getIcon(DDSortType.BySuspicionIndex)}
          </ListItemIconStyled>
          <SortTypeSelection
            primary={isMobile ? "Suspicion Index" : "By Suspicion Index"}
            selected={props.sortType === DDSortType.BySuspicionIndex}
          />
        </ListItem>
        <ListItem
          button
          onClick={() => {
            handleSelectSort(DDSortType.ByOrganSystem);
          }}
        >
          <ListItemIconStyled>
            {getIcon(DDSortType.ByOrganSystem)}
          </ListItemIconStyled>
          <SortTypeSelection
            primary={isMobile ? "Organ System" : "By Organ System"}
            selected={props.sortType === DDSortType.ByOrganSystem}
          />
        </ListItem>
      </List>
      <Divider />
    </div>
  );

  return (
    <SortSelectView>
      <DesktopOnly>
        <AgentTooltip title={"Sort"}>
          <SortButton onClick={handleClick} className={"sort-button"}>
            <SortIcon>
              <SortIconImg src={SortIconSVG} alt={"Sort Icon"} />
            </SortIcon>
          </SortButton>
        </AgentTooltip>

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem
            value={DDSortType.BySuspicionIndex}
            onClick={() => {
              handleSelectSort(DDSortType.BySuspicionIndex);
            }}
          >
            {getIcon(DDSortType.BySuspicionIndex)} By Suspicion Index
          </MenuItem>
          <MenuItem
            value={DDSortType.ByOrganSystem}
            onClick={() => {
              handleSelectSort(DDSortType.ByOrganSystem);
            }}
          >
            {getIcon(DDSortType.ByOrganSystem)}
            By Organ System
          </MenuItem>
        </Menu>
      </DesktopOnly>

      <MobileOnly>
        <>
          <SortButton onClick={toggleDrawer(true)}>
            <SortIcon>
              <SortIconImg src={SortIconSVG} alt={"Sort Icon"} />
            </SortIcon>
          </SortButton>

          <Drawer
            anchor={"bottom"}
            open={drawerOpen}
            onClose={toggleDrawer(false)}
          >
            <DrawerTitle>Sort By:</DrawerTitle>
            {list()}
          </Drawer>
        </>
      </MobileOnly>
    </SortSelectView>
  );
};
